import type { UniversalIssueWithScreenshot } from '@deque/axe-devtools-issues-utils';
import React, {
  type Dispatch,
  type SetStateAction,
  useState,
  useMemo,
  useEffect,
  useRef
} from 'react';
import {
  Modal,
  ModalContent,
  ModalFooter,
  Select,
  Button,
  Toast,
  Loader
} from '@deque/cauldron-react';
import { Trans, useTranslation } from 'react-i18next';

import {
  type SupportedIntegrationProductSlugs,
  ProductNames,
  type ProductSlugs
} from '../../constants';
import {
  type IssueToIntegrationSend,
  type HttpError,
  sendIssue
} from '../../api-client';
import analyticsInstances, {
  getFallbackAnalyticsInstanceId
} from '../../analyticsInstances';
import { getIntegrationProductInfoFromSlug } from '../../utils/get-integration-product-name-from-slug';
import useIntegrationConnections from '../../../app/hooks/useIntegrationConnections';
import useIntegrationTemplates from '../../../app/hooks/useIntegrationTemplates';
import useIntegrationProjects from '../../../app/hooks/useIntegrationProjects';
import { useGlobalToast } from '../../contexts/globalToast';
import { useEnterprises } from '../../contexts/enterprises';
import { useAuthContext } from '../../contexts/auth';
import Link from '../Link';
import styles from './ShareToIntegrationModalV1.css';

export interface ShareToIntegrationModalV1Props {
  integrationProductSlug: SupportedIntegrationProductSlugs;
  setShow: Dispatch<SetStateAction<boolean>>;
  issues: UniversalIssueWithScreenshot[];
  testId: string;
  issuesProductSourceSlug: ProductSlugs;
  multiple?: boolean;
  onIntegrationShare: () => void;
}

const ShareToIntegrationModalV1 = ({
  integrationProductSlug,
  setShow,
  issues,
  testId,
  issuesProductSourceSlug,
  onIntegrationShare,
  multiple = false
}: ShareToIntegrationModalV1Props) => {
  const { t } = useTranslation();
  const analytics =
    analyticsInstances[getFallbackAnalyticsInstanceId(integrationProductSlug)];

  const { setContents } = useGlobalToast();
  const { user } = useAuthContext();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const token = user!.token;
  const { activeEnterprise } = useEnterprises();

  const [connectionId, setConnectionId] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedIssueType, setSelectedIssueType] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [sendIssueError, setSendIssueError] = useState('');

  const connectionSelectRef = useRef<HTMLSelectElement>(null);

  const {
    data: connections,
    loading: connectionsLoading,
    error: connectionsError
  } = useIntegrationConnections(activeEnterprise?.id, token);

  const {
    data: projects,
    loading: projectsLoading,
    error: projectsError
  } = useIntegrationProjects(activeEnterprise?.id ?? '', connectionId, token);

  const {
    getTemplatesByProjectAndIssueTypeId,
    loading: templatesLoading,
    error: templatesError
  } = useIntegrationTemplates(
    connectionId,
    activeEnterprise?.id ?? '',
    token,
    integrationProductSlug
  );

  const connectedIntegrationsForProduct =
    connections?.filter(
      ({ status, product_slug }) =>
        status === 'connected' && product_slug === integrationProductSlug
    ) ?? [];
  const hasSingleConnectedIntegration =
    connectedIntegrationsForProduct &&
    connectedIntegrationsForProduct.length === 1;

  useEffect(() => {
    if (hasSingleConnectedIntegration) {
      setConnectionId(connectedIntegrationsForProduct[0].id);
    }
  }, [hasSingleConnectedIntegration]);

  const loading =
    connectionsLoading || projectsLoading || templatesLoading || shareLoading;

  useEffect(() => {
    if (!loading && !hasSingleConnectedIntegration && connectionId) {
      connectionSelectRef.current?.focus();
    }
  }, [loading, hasSingleConnectedIntegration, connectionId]);

  const error = connectionsError || projectsError || templatesError;

  const capitalizedIntegrationName = getIntegrationProductInfoFromSlug(
    integrationProductSlug as unknown as ProductSlugs
  ).displayName;

  const onShare = async () => {
    const projectEntity = projects?.find(
      project => project.id === selectedProject
    );
    const selectedProjectName = projectEntity?.name || '';
    const selectedIssueTypeName =
      projectEntity?.issueTypes?.find(
        issueType => issueType.id === selectedIssueType
      )?.name || '';
    const selectedTemplateEntity = getTemplatesByProjectAndIssueTypeId(
      selectedProject,
      selectedIssueType
    )?.find(template => template.id === selectedTemplate);
    const selectedTemplateName = selectedTemplateEntity?.name || '';

    const analyticsData = {
      integration: integrationProductSlug,
      project: selectedProjectName,
      issueType: selectedIssueTypeName,
      template: selectedTemplateName,
      multiple
    };

    analytics.integrationIssueSendClick(analyticsData);

    //TODO: add fingerprint handling
    const issuesToSend: IssueToIntegrationSend[] = issues
      .map((issue, index) => {
        const issueId =
          issue.issueId ||
          [selectedProject, issue.pageStateKey, issue.ruleId, index].join('_');

        return { ...issue, issueId };
      })
      .filter((issue): issue is IssueToIntegrationSend => !!issue.shareURL);

    setSendIssueError('');
    setContents(null);
    setShareLoading(true);
    try {
      await sendIssue(token, activeEnterprise?.id || '', connectionId, {
        issues: issuesToSend,
        group_id: testId,
        product_source_slug: issuesProductSourceSlug,
        template_id: selectedTemplate
      });
      analytics.integrationIssueSendSuccess(analyticsData);

      setContents(
        t(`{{capitalizedIntegrationName}} {{issue}} sent`, {
          issue: multiple ? 'issues' : 'issue',
          capitalizedIntegrationName
        }),
        'confirmation'
      );

      onIntegrationShare?.();
      setShow(false);
    } catch (err) {
      const e = err as HttpError;
      analytics.integrationIssueSendFail(analyticsData);
      // eslint-disable-next-line no-console
      console.error('Error returned from Jira', e.details);

      const errorMessage = e.details?.length
        ? t(
            `Error with sending {{multipleText}} to {{ capitalizedIntegrationName }}. The following fields are invalid: {{ errorFields }}`,
            {
              multipleText: multiple ? t('all issues') : t('issue'),
              capitalizedIntegrationName,
              errorFields: e.details.join(', ')
            }
          )
        : t(
            `Error with sending {{multipleText}} to {{ capitalizedIntegrationName }}`,
            {
              multipleText: multiple ? t('all issues') : t('issue'),
              capitalizedIntegrationName
            }
          );

      setSendIssueError(errorMessage);
    } finally {
      setShareLoading(false);
    }
  };

  const availableTemplates = useMemo(
    () =>
      getTemplatesByProjectAndIssueTypeId(selectedProject, selectedIssueType),
    [selectedProject, selectedIssueType, getTemplatesByProjectAndIssueTypeId]
  );

  return (
    <Modal
      heading={t('Share {{ multiple }}to {{ capitalizedIntegrationName }}', {
        multiple: multiple ? t('All ') : '',
        capitalizedIntegrationName
      })}
      show
      onClose={() => setShow(false)}
      className={styles.shareModal}
    >
      {loading && (
        <Loader
          label={t(
            'Share {{ multiple }}to {{ capitalizedIntegrationName }} modal loading',
            {
              multiple: multiple ? t('all ') : '',
              capitalizedIntegrationName
            }
          )}
        />
      )}
      {error && !loading && (
        <p>{t('Error loading project, issue type, and/or template data')}</p>
      )}
      {!loading && !error && (
        <>
          {!!selectedIssueType && !availableTemplates?.length && (
            <Toast type="error" dismissible={false} show>
              <Trans>
                No templates available for selected project and issue type.
                Please{' '}
                <Link
                  url={`/configuration/integrations/${connectionId}/templates`}
                  className={styles.errorToastLink}
                >
                  configure a template
                </Link>{' '}
                before sharing issue to{' '}
                {{
                  capitalizedIntegrationName
                }}
                .
              </Trans>
            </Toast>
          )}
          {!!sendIssueError && (
            <Toast
              show
              onDismiss={() => setSendIssueError('')}
              type="error"
              className={styles.chooseTemplateErrorToast}
            >
              {sendIssueError}
            </Toast>
          )}
          <ModalContent>
            <p>
              {t(
                `Select the template to send the {{ axe }} issues to. This will create one {{ capitalizedIntegrationName }} ticket {{ multipleIssueText }}.`,
                {
                  axe: ProductNames.axe,
                  capitalizedIntegrationName,
                  multipleIssueText: multiple
                    ? t(`with all {{ axe }} filtered issues`, {
                        axe: ProductNames.axe
                      })
                    : t(`for the selected {{ axe }} issue`, {
                        axe: ProductNames.axe
                      })
                }
              )}
            </p>
            {connectedIntegrationsForProduct &&
              connectedIntegrationsForProduct.length > 1 && (
                <Select
                  label={t('Connection')}
                  value={connectionId}
                  onChange={e => {
                    if (connectionId !== e.target.value) {
                      setSelectedProject('');
                      setSelectedIssueType('');
                      setSelectedTemplate('');
                    }
                    setConnectionId(e.target.value);
                  }}
                  ref={connectionSelectRef}
                  required
                >
                  <option value="">{t('Choose connection')}</option>
                  <>
                    {connectedIntegrationsForProduct?.map(connection => (
                      <option key={connection.id} value={connection.id}>
                        {connection.base_url}
                      </option>
                    ))}
                  </>
                </Select>
              )}
            <Select
              label={t('Project')}
              value={selectedProject}
              onChange={e => {
                if (selectedProject !== e.target.value) {
                  setSelectedIssueType('');
                  setSelectedTemplate('');
                }
                setSelectedProject(e.target.value);
              }}
              disabled={!connectionId}
              required
            >
              <option value="">{t('Choose project')}</option>
              <>
                {projects?.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </>
            </Select>
            <Select
              label={t('Issue Type')}
              value={selectedIssueType}
              onChange={e => {
                if (selectedIssueType !== e.target.value) {
                  setSelectedTemplate('');
                }
                setSelectedIssueType(e.target.value);
              }}
              disabled={!selectedProject}
              required
            >
              <option value="">{t('Choose issue type')}</option>
              <>
                {projects
                  ?.find(project => project.id === selectedProject)
                  ?.issueTypes?.map(issueType => (
                    <option key={issueType.id} value={issueType.id}>
                      {issueType.name}
                    </option>
                  ))}
              </>
            </Select>
            <Select
              label={t('Template')}
              value={selectedTemplate}
              onChange={e => setSelectedTemplate(e.target.value)}
              disabled={
                !selectedProject ||
                !selectedIssueType ||
                availableTemplates?.length === 0
              }
              required
            >
              <option value="">{t('Choose template')}</option>
              <>
                {availableTemplates?.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </>
            </Select>
          </ModalContent>
        </>
      )}
      <ModalFooter>
        <Button
          onClick={onShare}
          disabled={
            !selectedProject ||
            !selectedIssueType ||
            !selectedTemplate ||
            loading ||
            !!error
          }
        >
          {t('Send')}
        </Button>
        <Button variant="secondary" onClick={() => setShow(false)}>
          {t('Cancel')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ShareToIntegrationModalV1;
